import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Button, Row, Col, Alert, Clearfix } from 'react-bootstrap';
import { injectIntl } from 'react-intl';

import { getSalutationOptions, getGenderForSalutation } from 'helpers/clients';
import { formatEntityErrorsForDisplay } from 'helpers/formStatus';

import Tabs from 'components/layout/Tabs';
import Loader from 'components/layout/Loader';
import ClientProfile from 'components/layout/client/ClientProfile';
import ClientFinancial from 'components/layout/client/ClientFinancial';
import ClientVendor from 'components/layout/client/ClientVendor';
// import ClientRoles from 'components/layout/client/ClientRoles';

import EntityStateUpdater from 'component/EntityStateUpdater';
import Data from 'components/Data';
import Entity from 'components/Entity';

import FormInput from 'components/forms/FormInput';
import FormAutocomplete from 'components/forms/FormAutocomplete';
import FormSwitchery from 'components/forms/FormSwitchery';
import FormErrors from 'components/forms/FormErrors';

import { entityMetaMapping, DEFAULT_CLIENT_ROLES } from 'js/constants';
import List from 'components/List';

class ClientForm extends EntityStateUpdater {
  constructor(props) {
    super(props);

    this.state = {
      entityLoaded: false,
      entityChanged: false,
      sendInvitation: false,
      showLoadCompanyData: false,
    };

    this.entity = new Entity();

    // if we get user data from the outside, we can pass it on to our data entity
    const data = props.data || { has_accepted_terms_and_conditions: false };
    // if we don't have roles, we need to set defaults
    if (data.roles === undefined) {
      data.roles = DEFAULT_CLIENT_ROLES;
    }
    // if we don't have profile information, we need to set defaults
    if (data.profile === undefined) {
      data.profile = {
        country: props.user.profile.country,
        timezone: props.user.profile.timezone,
        salutation: 'mr',
        gender: 'male',
      };
    }
    // if we don't have financial information, we need to set defaults
    if (data.financial === undefined) {
      data.financial = {
        currency: props.user.financial.currency,
        vat_id_name: props.user.financial.vat_id_name,
        vat_rates: props.user.financial.vat_rates,
      };
    }

    // maybe a preset parent was passed through props
    if (!data.parent && props.parent) {
      data.parent = { ...props.parent.data };
    }

    this.entity.setData(data);

    if (props.meta[entityMetaMapping.client] !== undefined && !props.meta[entityMetaMapping.client].pending) {
      this.entity.setMeta(props.meta, entityMetaMapping.client);

      this.state = {
        ...this.state,
        entityLoaded: true,
        entity: this.entity,
      };
    }
  }

  showLoadCompanyData = e => {
    e.preventDefault();
    e.stopPropagation();

    this.setState({ showLoadCompanyData: true });
  };

  updateProfile = (name, value) => {
    this.updateEntity(name, value, ['profile']);
  };

  updateSalutation = (name, value) => {
    const gender = getGenderForSalutation(value);

    const { entity } = this.state;
    let newEntity = { ...entity.updateValue('salutation', value, ['profile']) };
    newEntity = { ...entity.updateValue('gender', gender, ['profile']) };
    this.changeEntity(newEntity);
  };

  updateCompany = (name, company) => {
    this.updateEntityMultiple(
      [
        // field names
        'company',
        'street',
        'houseNumber',
        'apartmentNumber',
        'addressLine2',
        'zipCode',
        'city',
        'creditorId',
        'currency',
        'vatIdName',
        'vatId',
        'vatName',
        'vatRates',
        'cocNumber',
        'registeredAt',
        'bookingAccountNumber',
        'costAccountNumber',
        'bankName',
        'accountHolder',
        'accountNumber',
        'bankLocation',
        'bankCodeNumber',
        'iban',
        'swiftBic',
      ],
      [
        // field values
        company.profile.company,
        company.profile.street,
        company.profile.house_number,
        company.profile.apartment_number,
        company.profile.zip_code,
        company.profile.city,
        company.financial.creditor_id,
        company.financial.currency,
        company.financial.vat_id_name,
        company.financial.vat_id,
        company.financial.vat_name,
        company.financial.vat_rates,
        company.financial.coc_number,
        company.financial.registered_at,
        company.financial.booking_account_number || company.financial.bookingAccountNumber,
        company.financial.cost_account_number || company.financial.costAccountNumber,
        company.financial.bank_data ? company.financial.bank_data.bank_name : '',
        company.financial.bank_data ? company.financial.bank_data.account_holder : '',
        company.financial.bank_data ? company.financial.bank_data.account_number : '',
        company.financial.bank_data ? company.financial.bank_data.bank_location : '',
        company.financial.bank_data ? company.financial.bank_data.bank_code_number : '',
        company.financial.bank_data ? company.financial.bank_data.iban : '',
        company.financial.bank_data ? company.financial.bank_data.swift_bic : '',
      ],
      [
        // field tree
        ['profile'],
        ['profile'],
        ['profile'],
        ['profile'],
        ['profile'],
        ['profile'],
        ['lang'],
        ['financial'],
        ['financial'],
        ['financial'],
        ['financial'],
        ['financial'],
        ['financial'],
        ['financial'],
        ['financial'],
        ['financial'],
        ['financial'],
        ['financial', 'bankData'],
        ['financial', 'bankData'],
        ['financial', 'bankData'],
        ['financial', 'bankData'],
        ['financial', 'bankData'],
        ['financial', 'bankData'],
        ['financial', 'bankData'],
      ],
      () => {
        // callback -> update the referenced fields
        this.refs.company.setValue(company.profile.company);
        this.refs.street.setValue(company.profile.street);
        this.refs.houseNumber.setValue(company.profile.house_number);
        this.refs.apartmentNumber.setValue(company.profile.apartment_number);
        this.refs.addressLine2.setValue(company.profile.address_line2);
        this.refs.zipCode.setValue(company.profile.zip_code);
        this.refs.city.setValue(company.profile.city);
      }
    );
  };

  componentDidUpdate = () => {
    const { meta } = this.props;

    if (
      meta[entityMetaMapping.client] !== undefined &&
      !meta[entityMetaMapping.client].pending &&
      !this.entity.getMetaLoaded()
    ) {
      this.entity.setMeta(meta, entityMetaMapping.client);
      if (this.entity.getDataLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }
  };

  formSubmit = e => {
    e.preventDefault();
    e.stopPropagation();

    this.setState(
      prevState => ({ entity: { ...prevState.entity.validate() } }),
      () => {
        if (this.state.entity.isValid) {
          this.props.onSubmit({
            ...this.state.entity.processedData,
            sendInvitation: !!(this.state.entity.getProfile().getEmail() && this.state.sendInvitation),
          });
        }
      }
    );
  };

  render() {
    const {
      intl: { messages },
      formStatus,
      type,
      languages,
    } = this.props;
    const { entity, entityLoaded } = this.state;

    const disabled = formStatus.pending;
    // const financial = entityLoaded && entity.getFinancial();
    let tabMessages = [messages.client, messages.profile, messages.financial];
    const isVendor = !!entity?.data?.roles?.includes?.('ROLE_VENDOR');

    if (isVendor) {
      tabMessages = [
        messages.client + ' (' + messages.basic_client_settings + ')',
        messages.profile,
        messages.financial,
        messages.vendor,
      ];
    } else {
      tabMessages = [
        messages.client + ' (' + messages.basic_client_settings + ')',
        messages.profile,
        messages.financial,
      ];
    }

    return (
      <Row>
        <Data dataName="client" /> {/* fetch meta data */}
        <List noRender listName="languages" url="/api/languages.json" list={languages} />
        <Col xs={12}>
          <FormErrors formStatus={formStatus} additionalErrors={formatEntityErrorsForDisplay(entity, messages)} />

          {entityLoaded ? (
            <form method="post" onSubmit={this.formSubmit}>
              <div>
                <Tabs
                  tabStyle="arrow"
                  tabResponsiveStyle="stack"
                  tabs={tabMessages} /* messages.roles */
                  tabContent={[
                    <React.Fragment key="client">
                      <div className="panel panel-transparent">
                        <div className="panel-body">
                          {/* !this.state.showLoadCompanyData ? (
                            <Alert bsStyle="info" className="no-margin">
                              <Clearfix>
                                <p className="col-xs-6 no-padding">
                                  {messages.you_can_apply_company_data_from_company_profile}
                                </p>
                                <p className="text-right no-margin bold">
                                  <a href="#loadCompanyData" onClick={this.showLoadCompanyData}>
                                    {messages.load_company_data}
                                  </a>
                                </p>
                              </Clearfix>
                            </Alert>
                          ) : (
                            <>
                              <p className="form-group-title">{messages.load_company_data}</p>
                              <div className="form-group-attached">
                                <FormAutocomplete
                                  label={messages.company_lookup}
                                  name="companyLookup"
                                  onChange={this.updateCompany}
                                  autocompleteType="client"
                                  disabled={disabled}
                                  returnValueObject
                                />
                              </div>
                            </>
                          ) */}

                          <h4 className="new-client form-group-title">{messages.formGroup.basic_information}</h4>
                          <div className="form-group-attached">
                            <FormAutocomplete
                              autocompleteType="client"
                              label={messages.parent}
                              onChange={this.updateEntity}
                              urlFilters={{ role: 'ROLE_COMPANY' }}
                              name="parent"
                              value={entity.getParent()}
                              meta={entity.getParentMetaInformation()}
                              validation={entity.getParentValidationResult()}
                              disabled={disabled}
                            />
                            <FormInput
                              ref="company"
                              label={messages.company}
                              name="company"
                              value={entity.getProfile().getCompany()}
                              meta={entity.getProfile().getCompanyMetaInformation()}
                              validation={entity.getProfile().getCompanyValidationResult()}
                              onChange={this.updateProfile}
                              disabled={disabled}
                            />
                            <FormInput
                              label={messages.email}
                              name="email"
                              value={entity.getProfile().getEmail()}
                              meta={entity.getProfile().getEmailMetaInformation()}
                              validation={entity.getProfile().getEmailValidationResult()}
                              onChange={this.updateProfile}
                              disabled={disabled}
                            />
                          </div>

                          <h4 className="new-client form-group-title">{messages.formGroup.contact_person}</h4>
                          <div className="form-group-attached">
                            <FormAutocomplete
                              label={messages.salutation}
                              name="salutation"
                              value={entity.getProfile().getSalutation()}
                              meta={entity.getProfile().getSalutationMetaInformation()}
                              validation={entity.getProfile().getSalutationValidationResult()}
                              onChange={this.updateSalutation}
                              options={getSalutationOptions(messages)}
                              disabled={disabled}
                            />
                            <FormInput
                              label={messages.first_name}
                              name="firstName"
                              value={entity.getProfile().getFirstName()}
                              meta={entity.getProfile().getFirstNameMetaInformation()}
                              validation={entity.getProfile().getFirstNameValidationResult()}
                              onChange={this.updateProfile}
                              disabled={disabled}
                            />
                            <FormInput
                              label={messages.last_name}
                              name="lastName"
                              value={entity.getProfile().getLastName()}
                              meta={entity.getProfile().getLastNameMetaInformation()}
                              validation={entity.getProfile().getLastNameValidationResult()}
                              onChange={this.updateProfile}
                              disabled={disabled}
                            />
                          </div>

                          <h4 className="new-client form-group-title">{messages.formGroup.address_information}</h4>
                          <div className="form-group-attached">
                            <FormInput
                              ref="street"
                              label={messages.street}
                              name="street"
                              value={entity.getProfile().getStreet()}
                              meta={entity.getProfile().getStreetMetaInformation()}
                              validation={entity.getProfile().getStreetValidationResult()}
                              onChange={this.updateProfile}
                              disabled={disabled}
                            />

                            <Row className="clearfix">
                              <Col sm={6}>
                                <FormInput
                                  ref="houseNumber"
                                  label={messages.house_number}
                                  name="houseNumber"
                                  value={entity.getProfile().getHouseNumber()}
                                  meta={entity.getProfile().getHouseNumberMetaInformation()}
                                  validation={entity.getProfile().getHouseNumberValidationResult()}
                                  onChange={this.updateProfile}
                                  disabled={disabled}
                                />
                              </Col>
                              <Col sm={6}>
                                <FormInput
                                  ref="apartmentNumber"
                                  label={messages.apartment_number}
                                  name="apartmentNumber"
                                  value={entity.getProfile().getApartmentNumber()}
                                  meta={entity.getProfile().getApartmentNumberMetaInformation()}
                                  validation={entity.getProfile().getApartmentNumberValidationResult()}
                                  onChange={this.updateProfile}
                                  disabled={disabled}
                                />
                              </Col>
                              <Col sm={12}>
                                <FormInput
                                  ref="addressLine2"
                                  label={messages.address_line2}
                                  name="addressLine2"
                                  value={entity.getProfile().getAddressLine2()}
                                  meta={entity.getProfile().getAddressLine2MetaInformation()}
                                  validation={entity.getProfile().getAddressLine2ValidationResult()}
                                  onChange={this.updateProfile}
                                  disabled={disabled}
                                />
                              </Col>
                            </Row>

                            <Row className="clearfix">
                              <Col sm={6}>
                                <FormInput
                                  ref="zipCode"
                                  label={messages.zip_code}
                                  name="zipCode"
                                  value={entity.getProfile().getZipCode()}
                                  meta={entity.getProfile().getZipCodeMetaInformation()}
                                  validation={entity.getProfile().getZipCodeValidationResult()}
                                  onChange={this.updateProfile}
                                  disabled={disabled}
                                />
                              </Col>
                              <Col sm={6}>
                                <FormInput
                                  ref="city"
                                  label={messages.city}
                                  name="city"
                                  value={entity.getProfile().getCity()}
                                  meta={entity.getProfile().getCityMetaInformation()}
                                  validation={entity.getProfile().getCityValidationResult()}
                                  onChange={this.updateProfile}
                                  disabled={disabled}
                                />
                              </Col>
                            </Row>

                            <FormAutocomplete
                              ref="country"
                              label={messages.country}
                              name="country"
                              value={entity.getProfile().getCountry()}
                              meta={entity.getProfile().getCountryMetaInformation()}
                              validation={entity.getProfile().getCountryValidationResult()}
                              onChange={this.updateProfile}
                              autocompleteType="country"
                              complete
                              disabled={disabled}
                            />
                          </div>

                          <h4 className="new-client form-group-title">{messages.formGroup.contact_data}</h4>
                          <div className="form-group-attached">
                            <FormInput
                              label={messages.phone}
                              name="phone"
                              value={entity.getProfile().getPhone()}
                              meta={entity.getProfile().getPhoneMetaInformation()}
                              validation={entity.getProfile().getPhoneValidationResult()}
                              onChange={this.updateProfile}
                              disabled={disabled}
                              infoText={messages.client_settings_info_text.info_text_phone}
                            />
                            <FormInput
                              label={messages.mobile_phone}
                              name="mobile"
                              value={entity.getProfile().getMobile()}
                              meta={entity.getProfile().getMobileMetaInformation()}
                              validation={entity.getProfile().getMobileValidationResult()}
                              onChange={this.updateProfile}
                              disabled={disabled}
                              infoText={messages.client_settings_info_text.info_text_mobile}
                            />
                          </div>

                          <h4 className="new-client form-group-title">
                            {messages.languages} {languages.pending && <i className="m-l-10 fa fa-circle-notch fa-spin" />}
                          </h4>
                          <div className="form-group-attached">
                            <FormAutocomplete
                              label={messages.primary_language}
                              name="mainLanguage"
                              value={entity.getLang().getMainLanguage() || 'de'}
                              meta={entity.getLang().getMainLanguageMetaInformation()}
                              validation={entity.getLang().getMainLanguageValidationResult()}
                              onChange={(propertyName, value) => {
                                this.updateEntity(propertyName, value, ['lang']);
                              }}
                              options={languages.data.list.map(language => {
                                return { value: language.id, label: language.name };
                              })}
                              complete
                              infoText={messages.client_settings_info_text.info_text_main_language}
                            />
                          </div>
                        </div>
                      </div>
                    </React.Fragment>,
                    <ClientProfile
                      key="profile"
                      typeOfUser="customer"
                      type={type}
                      entityLoaded={entityLoaded}
                      entity={entity}
                      changeEntity={this.changeEntity}
                      disabled={formStatus.pending}
                      inModal
                    />,
                    <ClientFinancial
                      key="financial"
                      typeOfUser="customer"
                      entityLoaded={entityLoaded}
                      entity={entity}
                      type={type}
                      changeEntity={this.changeEntity}
                      disabled={formStatus.pending}
                      inModal
                    />,
                    isVendor && (
                      <ClientVendor
                        key="vendor"
                        typeOfUser="customer"
                        entityLoaded={entityLoaded}
                        entity={entity}
                        changeEntity={this.changeEntity}
                        disabled={formStatus.pending}
                        inModal
                      />
                    ),

                    /*
                    <ClientRoles
                      key="roles"
                      entityLoaded={entityLoaded}
                      entity={entity}
                      changeEntity={this.changeEntity}
                      disabled={formStatus.pending}
                      inModal
                    />,
                     */
                  ]}
                />

                <div className="panel panel-transparent">
                  <div className="panel-body">
                    <div className="form-group-attached">
                      <div className="form-group form-group-default">
                        <FormSwitchery
                          name="sendInvite"
                          label={
                            messages.send_invitation_with_username_and_password +
                            ' (' +
                            (entity.getProfile().getEmail() || '-') +
                            ')'
                          }
                          value
                          checked={!!(this.state.sendInvitation && entity.getProfile().getEmail())}
                          onChange={(name, value) => this.setState({ sendInvitation: value })}
                          disabled={!!(formStatus.pending || !entity.getProfile().getEmail())}
                        />
                      </div>
                    </div>

                    <div className="text-right m-t-15">
                      <Button
                        bsSize="lg"
                        disabled={formStatus.pending}
                        type="submit"
                        bsStyle={this.state.entityChanged ? 'primary' : 'default'}
                      >
                        <i className={'fa-fw ' + (formStatus.pending ? 'fa fa-circle-notch fa-spin' : 'fal fa-save')} />{' '}
                        {messages.save}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          ) : (
            <Loader />
          )}
        </Col>
      </Row>
    );
  }
}

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    meta: state.entityMeta,
    user: state.login.user,
    languages: state.list.languages,
  };
};

export default withRouter(injectIntl(connect(mapStateToProps)(ClientForm)));
